<template>
  <v-card color="background lighten-3" elevation="0" rounded="lg">
    <v-card-text>
      <v-row>
        <v-col class="d-flex align-center justify-center" cols="12" md="6">
          <t-credit-card :form-selectors="selectors" form="form#cc-form" />
        </v-col>
        <v-col cols="12" md="6" class="mt-5 mt-md-2">
          <v-form id="cc-form" ref="formRef">
            <v-row>
              <v-col cols="12" class="py-0">
                <t-text-input
                  id="cc-name"
                  v-model="data.card.holder.name"
                  label="Nome do Titular (como impresso no cartão)"
                  required
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <t-email-input v-model="data.card.holder.email" required />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" md="6" class="py-0">
                <t-document-input v-model="data.card.holder.document" required />
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <t-date-input v-model="data.card.holder.birth_date" label="Data de Nascimento" required />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0">
                <t-card-input
                  id="cc-number"
                  v-model="data.card.number"
                  required
                  label="Número do Cartão"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col md="6" cols="12" class="py-0">
                <t-text-input
                  id="cc-expiration"
                  v-model="data.card.expiration"
                  :mask="'##/##'"
                  required
                  label="Data de Validade"
                  placeholder="MM/AA"
                />
              </v-col>
              <v-col md="6" cols="12" class="py-0">
                <t-text-input
                  id="cc-cvv"
                  v-model="data.card.cvv"
                  :mask="'###'"
                  required
                  label="Código de Segurança"
                />
              </v-col>
            </v-row>

            <v-row>
              <v-col md="8" cols="12" class="py-0">
                <div v-if="failed" class="d-flex align-center justify-space-between mb-6">
                  <span class="text-error text-body-2">Não foi possível consultar as parcelas.</span>
                  <v-btn variant="text" color="primary" @click="retry">
                    Tentar novamente
                  </v-btn>
                </div>
                <t-select
                  v-else
                  v-model="data.installments"
                  :items="planItems"
                  label="Parcelamento"
                  :disabled="plans.length === 0"
                  :loading="loadingPlans"
                />
              </v-col>
              <v-col md="4" cols="12" class="py-0 mb-md-0 mb-4">
                <v-btn
                  color="success"
                  :loading="loader.loading('button')"
                  size="x-large"
                  block
                  @click="emit('submit')"
                >
                  Pagar
                </v-btn>
              </v-col>
            </v-row>
          </v-form>
        </v-col>
      </v-row>
    </v-card-text>
  </v-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";
import { useLoader } from "@/stores/loader";
import { useSnackbar } from "@/stores/snackbar";
import TDocumentInput from "@/components/core/TDocumentInput.vue";
import TDateInput from "@/components/core/TDateInput.vue";
import TCardInput from "@/components/core/TCardInput.vue";
import TEmailInput from "@/components/core/TEmailInput.vue";
import TTextInput from "@/components/core/TTextInput.vue";
import TCreditCard from "@/components/core/TCreditCard.vue";
import TSelect from "@/components/core/TSelect.vue";
import getPaymentConfig from "@/api/requests/getPaymentConfig";
import CreditCard from "@/types/CreditCard";
import CheckoutResult from "@/types/CheckoutResult";
import PaymentData from "@/types/PaymentData";
import ThreedsService from "@/services/ThreedsService";
import PaymentMethod from "@/types/PaymentMethod";
import InstallmentPlan from "@/types/InstallmentPlan";
import { useUserStore } from "@/stores/user";
import { useCheckoutStore } from "@/stores/checkout";
import { useCartStore } from "@/stores/cart";
import { useInstallmentPlans } from "@/composables/useInstallmentPlans";
import { installmentPlanTitle } from "@/utils/installmentPlan";

interface Props {
  loading: boolean;
  method: PaymentMethod;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  submit: [];
}>();

const userStore = useUserStore();
const loader = useLoader();
const snackbar = useSnackbar();
const checkout = useCheckoutStore();
const cart = useCartStore();

const formRef = ref<{ validate: () => boolean } | null>(null);

const selectors = {
  numberInput: "input#cc-number",
  expiryInput: "input#cc-expiration",
  cvcInput: "input#cc-cvv",
  nameInput: "input#cc-name",
};

const data = ref<PaymentData>({
  installments: 1,
  card: {
    number: "",
    cvv: "",
    expiration: "",
    holder: {
      document: "",
      name: "",
      email: "",
      birth_date: "",
    },
  } as CreditCard,
  // Just so typescript stops yelling at the clouds
  tickets: {},
});

const { plans, loading: loadingPlans, failed, retry } = useInstallmentPlans(
  () => checkout.order?.id ?? "",
  () => data.value.card.number,
);

/** Shown until the card's plans arrive: the cart's total in 1x, which every card offers. */
const upfront = computed<InstallmentPlan>(() => {
  const amount = Math.round(cart.totalAmount * 100);

  return { installments: 1, installment_value: amount, interest_free: true, amount, interest: 0 };
});

const planItems = computed<{ title: string; value: number }[]>(() => {
  const offered = plans.value.length > 0 ? plans.value : [ upfront.value ];

  return offered.map(plan => ({
    title: installmentPlanTitle(plan),
    value: plan.installments,
  }));
});

const plan = computed<InstallmentPlan | null>(() => {
  return plans.value.find(p => p.installments === data.value.installments) ?? null;
});

// Another card's or Order's plans may not reach the installments chosen before, 1x is always among them
watch(plans, () => {
  if (plan.value === null && plans.value.length > 0) {
    data.value.installments = plans.value[0].installments;
  }
});

watch(plan, () => {
  checkout.plan = plan.value;
}, { immediate: true });

async function pay(): Promise<CheckoutResult> {
  // Checked by validate(), before the timer stops
  if (plan.value === null) {
    throw new Error("Informe o cartão e escolha em quantas parcelas pagar");
  }

  const service = new ThreedsService();
  await service.config();
  // The amount charged, Buyer interest included
  const result = await service.authenticate(
    plan.value.amount,
    userStore.data,
    data.value.card,
    data.value.installments,
  );

  if (result.status === "CHANGE_PAYMENT_METHOD") {
    throw new Error("Autenticação negada pela bandeira do cartão, tente outro método de pagamento");
  }

  if (result.status === "REQUIRE_CHALLENGE") {
    snackbar.show("Será necessário validar sua transação com o banco emissor do cartão", "success", 3000);
  }

  if (result.status === "AUTH_FLOW_COMPLETED") {
    // This could be used to prevent unauthenticated transactions from being completed, but would catch
    // a lot of false positives, so i'm disabling for now

    // if (result.authenticationStatus !== "AUTHENTICATED") {
    //   throw new Error("Transação não validada pelo motor de risco, tente outro método de pagamento");
    // }

    data.value.card.threeds = result.id;
  }

  // This is needed to create a deep copy and remove non-PCI compliant card data from server request
  const copy = JSON.parse(JSON.stringify(data.value)) as PaymentData;
  copy.card.bin = copy.card.number.replaceAll(" ", "").substring(0, 6);
  copy.card.number = "";
  copy.card.expiration = "";

  return await checkout.pay(props.method, copy);
}

async function validate(): Promise<boolean> {
  const result = formRef.value?.validate() ?? false;

  if (!result) return false;

  if (plan.value === null) {
    throw new Error("Informe o cartão e escolha em quantas parcelas pagar");
  }

  const config = await getPaymentConfig();

  const expMonth = data.value.card.expiration.split("/")[0];
  const expYear = "20" + data.value.card.expiration.split("/")[1];

  try {
    const pagseguro = window.PagSeguro.encryptCard({
      publicKey: config.creditcard.extra_fields.public_key,
      holder: data.value.card.holder.name,
      number: data.value.card.number.replaceAll(" ", ""),
      expMonth: expMonth,
      expYear: expYear,
      securityCode: data.value.card.cvv,
    });

    if (pagseguro.hasErrors) return false;

    data.value.card.encrypted = pagseguro.encryptedCard;

    return result;
  } catch (error) {
    console.log(error);
    return false;
  }
}

defineExpose({
  pay,
  validate,
});
</script>

<style>
#cc-form .col-12 {
  padding-bottom: 0;
  padding-top: 0;
}
</style>
